/**
 * Combine the parent and child layer data.
 */

import { cloneDeep } from 'lodash'
import get from 'lodash/get'
import toLower from 'lodash/toLower'
import OLSourceImageWMS from 'ol/source/ImageWMS'

import * as PoziUtils from 'api/utils'
import { MAX_GET_URL_LENGTH_GET, MAX_GET_URL_LENGTH_POST, UsePost } from 'drawer/tabs/info/download/Download'
import { featureCollectionIsSinglePoint } from 'utils/TurfUtils'

export default function GetChildCombinedURL (childLayer, currentTarget) {
  // console.debug('GetChildCombinedURL', childLayer.titleid, childLayer, currentTarget)

  let maxURLLength = MAX_GET_URL_LENGTH_GET

  let replaceCQLGeomAlternate = ''
  // Get a url with the full geometry
  let url = GetURL(childLayer, currentTarget, replaceCQLGeomAlternate)

  if (!url) {
    console.log('GetChildCombinedURL - No URL returned?', url)
    return url
  }

  // console.debug('GetChildCombinedURL - RAW url', url)

  // If the url is one of these... it is going to to a basic x-www-form-urlencoded POST request.
  // This increases the length.. it is still not infinite. The length is set by the web server IT manager e.g. DELWP.
  const bUsePost = UsePost(url)
  if (bUsePost) {
    maxURLLength = MAX_GET_URL_LENGTH_POST
  }

  // console.debug('GetChildCombinedURL - maxURLLength', childLayer.titleid, maxURLLength, bUsePost)

  // Get a copy of the properties to avoid unwanted side effects
  const propertiesAll = cloneDeep(currentTarget.propertiesAll || {})

  // console.debug('GetURL - propertiesAll', childLayer.titleid, propertiesAll)

  // Loop through the following fields until we find a value that makes the URL shorter than the max length
  // * $cqlgeometry: the originial full size CQL geometry
  // * $cqlgeometrysimplified: the simplified CQL geometry
  // * $cqlgeometrywhatshere: the 'click point' if available
  // * $cqlgeometrycentroid: the centroid of the CQL geometry

  const $cqlgeometryFields = ['$cqlgeometry', '$cqlgeometrysimplified', '$cqlgeometrywhatshere', '$cqlgeometrycentroid']
  for (const $cqlgeometryField of $cqlgeometryFields) {
    const fieldValue = propertiesAll[$cqlgeometryField]

    if (!fieldValue) {
      continue
    }

    const fieldValueLength = fieldValue.length

    // This is a rough length approximation as there may be more fields that will get replaced
    // with values. We could do a more precise calculation but it's not worth the effort.
    const urlLength = url.length + fieldValueLength

    // console.debug('GetChildCombinedURL - url property - $cqlgeometry field - length', $cqlgeometryField, fieldValueLength, urlLength)

    // Pick the current field
    replaceCQLGeomAlternate = $cqlgeometryField

    // We will use the current $cqlgeometry field if it is shorter than the max URL length.
    // Otherwise, we'll continue the loop until we found one or have exhausted all available fields.
    if (urlLength < maxURLLength) {
      break
    }
  }

  // console.debug('GetChildCombinedURL - $cql geometry field', childLayer.titleid, replaceCQLGeomAlternate)

  // If an alternative field to $cqlgeometry has been suggested and it exists in
  // the properties object, we conveniently replace the value of the field with
  // the value of the suggested alternative
  if (propertiesAll.$cqlgeometry && propertiesAll[replaceCQLGeomAlternate]) {
    propertiesAll.$cqlgeometry = propertiesAll[replaceCQLGeomAlternate]
  }

  // Use convenience function to replace variables
  url = PoziUtils.replaceUrlVariablesWithPropsValues(url, propertiesAll)

  // console.debug('GetChildCombinedURL - FNAL url', childLayer.titleid, url)

  return url
}

function GetURL (childLayer, currentTarget, replaceCQLGeomAlternate) {
  const typelower = toLower(get(childLayer, 'type', null))
  // console.debug('GetURL', childLayer.titleid, typelower, replaceCQLGeomAlternate)

  if (typelower === 'imagewms' || typelower === 'tilewms') {
    return GetFeatureInfoUrl(childLayer, currentTarget)
  }

  const url = PoziUtils.getLayerUrl(childLayer)

  // console.debug('GetURL - url - length', childLayer.titleid, url)

  return url
}

function GetFeatureInfoUrl (childLayerConf, currentTarget) {
  const { titleid } = childLayerConf
  // console.debug('GetFeatureInfoUrl', titleid, childLayerConf, currentTarget)

  const selectionIsSinglePoint = featureCollectionIsSinglePoint(currentTarget.featureCollection)

  // console.debug('GetFeatureInfoUrl - selectionIsSinglePoint?', titleid, selectionIsSinglePoint)

  // Only use alternative GFI when target is a single point or (multi) linestring and server can handle it (i.e. is QGIS)
  // Also, we take into account that the geometry type could be 3D (i.e. PointZ, LineStringZ, MultiLineStringZM, MultiCurve, etc.)
  const layerIsQgis = childLayerConf.serverType === 'qgis'
  const geometryType = childLayerConf.geometryType || ''
  const layerGeometryTypeIsPoint = !!geometryType?.match(/point/i)
  const layerGeometryTypeIsLineString = !!geometryType?.match(/line|curve/i)

  // Only use the FILTER_GEOM when the server can handle it.
  // Do not use this for a single click on a point or line layer, as we have not implemented support for
  // a hit tolerance for WMS point/line features yet.
  const useQgisGeomFilter = layerIsQgis &&
    !(selectionIsSinglePoint && (layerGeometryTypeIsPoint || layerGeometryTypeIsLineString))

  // console.debug('GetFeatureInfoUrl - useQgisGeomFilter?', titleid, useQgisGeomFilter, selectionIsSinglePoint, layerGeometryTypeIsPoint, layerGeometryTypeIsLineString)
  // console.debug('GetFeatureInfoUrl - QGIS geometry type?', titleid, childLayerConf.geometryType)

  let url = ''
  if (useQgisGeomFilter) {
    url = getFeatureInfoUrlForQgisServer(childLayerConf, currentTarget)
  } else {
    url = getFeatureInfoUrlFromOlWmsSource(childLayerConf, currentTarget)
  }

  console.debug('GetFeatureInfoUrl - url', titleid, url)

  return url
}

const getFeatureInfoUrlForQgisServer = (childLayerConf, currentTarget) => {
  const { titleid } = childLayerConf

  console.debug('getFeatureInfoUrlForQgisServer', titleid, childLayerConf, currentTarget)

  const currentTargetFC = currentTarget.featureCollection

  const wmsParams = {
    INFO_FORMAT: 'application/json',
    FEATURE_COUNT: 10000000, // if not provided, results will be limited to 1
    WITH_GEOMETRY: 'TRUE',
    CRS: 'EPSG:4326',
    REQUEST: 'GetFeatureInfo',
    FILTER_GEOM: '[$wkt]', // this will be replaced with the WKT of the feature geometry when doing the request
    QUERY_LAYERS: childLayerConf.config?.params?.LAYERS,
    WIDTH: 1, // somehow the WIDTH and the HEIGHT parameters are required by QGIS
    HEIGHT: 1 // somehow the WIDTH and the HEIGHT parameters are required by QGIS
  }

  const url = PoziUtils.getLayerUrl(childLayerConf, wmsParams)

  // console.debug('getFeatureInfoUrlForQgisServer - url', titleid, url)

  return url
}

// Use the functionality built in the OL code to generate a GFI URL. We do this by creating an OLSourceImageWMS
// and have that generate the URL
const getFeatureInfoUrlFromOlWmsSource = (childLayerConf, currentTarget) => {
  console.debug('getFeatureInfoUrlFromOlWmsSource', childLayerConf.titleid, childLayerConf, currentTarget)

  const app = PoziUtils.getApp()

  // Use the location from the map marker. If that is not available, use the centroid of
  // the currentTarget.
  // Note: we should only do this if the target is a single point, single line or single polygon.
  //       For the polygon, we should calculate a point on surface, not the centroid!
  // TODO: improve this code to only create a URL if the above conditions are met.
  const positionFromMarker = app.selectMarker.getPosition()
  const currentTargetCentroid = app.transformFrom4326(currentTarget.featureCollectionCentroid?.geometry?.coordinates)
  const position = positionFromMarker || currentTargetCentroid

  // console.debug('getFeatureInfoUrlFromOlWmsSource - position from marker / centroid', childLayerConf.titleid, positionFromMarker, currentTargetCentroid)

  if (!position) {
    console.log('getFeatureInfoUrlFromOlWmsSource - no url due to no marker position', childLayerConf.titleid, app.selectMarker, currentTargetCentroid, currentTarget)

    return null
  }

  // Create options for WMS source from some default values and the layer config
  const wmsSourceOptions = {
    projection: 'EPSG:4326',
    crossOrigin: 'Anonymous',

    ...childLayerConf.config
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - wmsSourceOptions', wmsSourceOptions)

  // Here we assume that the URL for the layer is in 'config.url' (and not 'config.spatial.url')
  const wmsSource = new OLSourceImageWMS(wmsSourceOptions)

  // Create params object for GFI request from default settings and the layer config
  const getFeatureInfoDefaultParams = {
    INFO_FORMAT: 'application/json',
    FEATURE_COUNT: 10000000, // if not provided, results will be limited to 1
    BUFFER: '5',
    FORMAT: 'image/png8',
    FI_POINT_TOLERANCE: '10',
    FI_LINE_TOLERANCE: '10',
    FI_POLYGON_TOLERANCE: '5',
    WITH_GEOMETRY: 'TRUE'
  }

  const childLayerParams = childLayerConf.config?.spatial?.params || childLayerConf.config?.params || {}

  const getFeatureInfoParams = {
    ...getFeatureInfoDefaultParams,
    ...childLayerParams
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - getFeatureInfoParams', getFeatureInfoParams)

  const mapView = app.map.getView()
  const resolution = mapView.getResolution()
  const projection = mapView.getProjection()

  let url
  try {
    url = wmsSource.getFeatureInfoUrl(position, resolution, projection, getFeatureInfoParams)
  } catch (error) {
    console.log('getFeatureInfoUrlFromOlWmsSource - error', childLayerConf.titleid, url, error)
  }

  // console.debug('getFeatureInfoUrlFromOlWmsSource - url', childLayerConf.titleid, url)

  return url
}
